import { Injectable, inject } from "@angular/core";
import { Data } from "@angular/router";
import { throwError } from "rxjs";
import { catchError, switchMap, take, tap } from "rxjs/operators";
import { StoreJackpotBottomsheetComponent } from "../store-jackpot-bottomsheet/store-jackpot-bottomsheet.component";
import { StoreJackpotTriggerDataService } from "./store-jackpot-trigger-data.service";

/**
 * Facade that owns the jackpot-trigger interaction flow previously embedded in
 * `StorePage` (activation/payment, trigger details bottom sheet, pre-auth
 * listener and trigger opening). Each method receives the host `StorePage`
 * instance so behaviour and state mutations stay byte-for-byte identical.
 */
@Injectable()
export class StoreJackpotTriggerFacadeService {
  private jackpotTriggerDataService = inject(StoreJackpotTriggerDataService);

  // Registers the pre-auth completion listener that was previously created in
  // the StorePage constructor.
  initPreAuthListener(host: any): void {
    host.paymentService?.jackpotPreAuthDone$?.subscribe((status) => {
      if (status === "yes") {
        host.isJptActived.set(false);
        host.showDivSlider = false;
        host.paymentService?.preAuthActivateTriggerResponse$?.subscribe((res) => {
          if (res?.status === "rejected") {
            host.authService
              ?.getConstantsMsg(res?.error_code)
              ?.then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Something went wrong!",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                host.authService.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((error) => {
                return error;
              });
            return;
          }
          if (res?.status === "FAIL") {
            host.authService
              ?.getConstantsMsg("1071")
              ?.then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Process failed, Please try again",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                host.authService?.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((error) => {
                return error;
              });
          } else {
            try {
              const jackpotName = host.jackpotName ?? JSON.parse(localStorage.getItem("JackpotTriggerData"))?.jackpot_name;
              host.authService
                ?.getConstantsMsg("1072")
                .then((translatedMsg) => {
                  const defaultMsg = {
                    message: {
                      line_1: `${jackpotName} Jackpot Trigger activated`,
                      line_2: "",
                    },
                    style: {
                      icon: "tick circle success.png",
                      type: "Success",
                    },
                  };
                  translatedMsg.message.line_1 = String(jackpotName + translatedMsg?.message?.line_1);
                  host.authService?.showToastMessage(translatedMsg, defaultMsg);
                })
                .catch((error) => {
                  return error;
                });
              localStorage.removeItem("JackpotTriggerData");
              host.storeService.showJackpotPromoSection$.next(true);
            } catch (e) {
              throw new Error("function throws the error" + e);
            }
          }
        });
      } else if (status === "no") {
        host.authService
          ?.getConstantsMsg("1073")
          ?.then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Authorisation failed. Please try again",
                line_2: "",
              },
              style: {
                icon: "tick circle error.svg",
                type: "Error",
              },
            };
            host.authService.showToastMessage(translatedMsg, defaultMsg);
          })
          .catch((error) => {
            return error;
          });
      }
    });
  }

  openTriggerDetails(host: any, triggerData: Data): void {
    host.celebrationAnimationService.isXpStatus.set(false); // Set isXpStatus to false;
    if (!host.fragmentAlias) {
      this.jackpotTriggerDataService
        .getAliasName(triggerData?.alias)
        .then((alias) => {
          triggerData.alias = alias;
          this.openJackpotBottomSheet(host, triggerData);
        })
        .catch(() => {
          host.authService.getConstantsMsg("1026").then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Something went wrong, please try again later.",
                line_2: "",
              },
              style: {
                type: "Error",
                icon: "tick circle error.svg",
              },
            };
            host.authService.showToastMessage(translatedMsg, defaultMsg);
          });
        });
    } else {
      this.openJackpotBottomSheet(host, triggerData);
    }
  }

  openJackpotBottomSheet(host: any, triggerData: Data): void {
    host.jackpotName = triggerData?.jackpot_name;
    const bottomSheetRef = host.bottomSheet.open(StoreJackpotBottomsheetComponent, {
      panelClass: "store-jackpot-bottomsheet",
      backdropClass: "about-product-dialog-backdrop",
      data: { ...triggerData, trigger_active_count: host.triggerActiveCount, isJptActived: host.isJptActived() },
    });
    bottomSheetRef.afterDismissed().subscribe((response: Data) => {
      const isSuccess = response?.status === "success";
      host.clarityService.event("jackpot_trigger_activated");
      host.clarityService.setTag("product_type", "jackpot_trigger");
      host.triggerActivationStatus.set(isSuccess);
    });
  }

  onAnimationComplete(host: any): void {
    if (host.triggerActivationStatus()) {
      host.triggerActivationStatus.set(false);
      host.jackpotTriggerService.setXpAnimationStop(false);
      // Trigger gem wallet animation
      host.xpsDetailSubscription = host.gameCelebrationService?.getXpsDetailsData$.subscribe((res: Data) => {
        try {
          // This variable contain XPS Value
          const XpsValues = res?.details;
          const isXpStatus = host.celebrationAnimationService.isXpStatus();
          if (!XpsValues) return;
          if (!isXpStatus) {
            host.celebrationAnimationService.gemWalletAnimation("gem_wallet", false);
          }
        } catch (error) {
          return throwError(error);
        }
      });
    }
  }

  openJackpotTrigger(host: any, trigger: string): void {
    host.sliderAliasSub = host.sliderManagerService?.alias$
      .pipe(
        take(1),
        tap((alias: string) => {
          host.fragmentAlias = alias ? alias : "std";
        }),
        switchMap(() => host.sliderJackpot$),
        take(1)
      )
      ?.subscribe(
        (res: Data) => {
          if (!res) return;
          const filterJackpotTrigger = res.filter((element) => {
            if (element && trigger?.toLowerCase()?.includes(element?.jackpot_name?.toLowerCase())) {
              return true;
            }
          });
          const isTriggerActive = filterJackpotTrigger?.some((element) => element?.status);
          if (!isTriggerActive) {
            filterJackpotTrigger.forEach((element) => {
              if (
                host.fragmentAlias &&
                host.fragmentAlias !== "" &&
                element?.alias?.toLowerCase().includes(host.fragmentAlias?.toLowerCase())
              ) {
                this.openTriggerDetails(host, element);
                host.fragmentAlias = "";
              }
            });
          } else {
            host.authService?.getConstantsMsg("1123").then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: `Jackpot trigger already activated`,
                  line_2: "",
                },
                style: {
                  icon: "tick circle success.png",
                  type: "Success",
                },
              };
              host.authService?.showToastMessage(translatedMsg, defaultMsg);
            });
          }
        },
        (err) => {
          return err;
        }
      );
  }

  // This method has been used to open payment page
  openPaymentPage(host: any, data: Data): void {
    host.isJptActived.set(true);
    let promoName = localStorage.getItem("engagement_promo") || "";
    if (promoName === "jackpot_trigger_offer") {
      host.authService.displayPromo("seen", promoName);
      host.authService.displayPromo("click", promoName);
    }
    const {
      jackpot_name,
      lines,
      jackpot,
      price,
      offer_link_id,
      offer_id,
      free_jackpot = false,
      alternate_free_jackpot = false,
      free_offer_id = 0,
      alias = "",
    } = data || {};

    host.jackpotName = jackpot_name;
    host.jackpotTriggerObj = {
      lines,
      jackpot,
      price,
      offer_link_id,
      offer_id,
      free_jackpot,
      alternate_free_jackpot,
      free_offer_id,
      alias,
    };

    host.GTMSagmentService.HotJarEvent("user_activate_trigger");
    host.accountService.syndicatePromotion$.pipe(take(1)).subscribe((res) => {
      if (res) {
        host.jackpotTriggerObj["call_from"] = "jackpot_trigger_promotion";
      }
    });
    host.authService.userDetails$.pipe(take(1)).subscribe(
      (user: Data) => {
        let userTokenExists = user?.token_exists;
        let promoName = user?.engagement_promo ? user?.engagement_promo?.current : {};

        if (userTokenExists) {
          host.syndicateService
            .activateTrigger(host.jackpotTriggerObj)
            .pipe(
              catchError((error) => {
                // RB #24242 fix
                return throwError(error);
              })
            )
            .subscribe(
              (res: Data) => {
                const isSuccess = res?.status === "success";
                host.isJptActived.set(false);
                if (promoName === "jackpot_trigger_offer") {
                  host.authService.displayPromo("conversion", promoName);
                }
                if (isSuccess) {
                  host.jackpotTriggerService.setXpAnimationStop(isSuccess);
                }
                host.jackpotTriggerService.setTriggerActivationStatus(isSuccess);
                host.response.next(res); // Notify subscribers of the response
                if (res?.status === "rejected") {
                  host.authService
                    .getConstantsMsg(res?.error_code)
                    .then((translatedMsg) => {
                      const defaultMsg = {
                        message: {
                          line_1: "Something went wrong!",
                          line_2: "",
                        },
                        style: {
                          icon: "tick circle error.svg",
                          type: "Error",
                        },
                      };
                      host.authService.showToastMessage(translatedMsg, defaultMsg);
                    })
                    .catch((error) => {
                      return error;
                    });
                  return;
                }
                if (res?.status === "fail") {
                  host.authService
                    .getConstantsMsg("1071")
                    .then((translatedMsg) => {
                      const defaultMsg = {
                        message: {
                          line_1: "Process failed, Please try again",
                          line_2: "",
                        },
                        style: {
                          icon: "tick circle error.svg",
                          type: "Error",
                        },
                      };
                      host.authService.showToastMessage(translatedMsg, defaultMsg);
                    })
                    .catch((error) => {
                      return error;
                    });
                } else {
                  host.clarityService.event("jackpot_trigger_activated");
                  host.clarityService.setTag("product_type", "jackpot_trigger");
                  try {
                    host.authService
                      .getConstantsMsg("1072")
                      .then((translatedMsg) => {
                        const defaultMsg = {
                          message: {
                            line_1: `${host.jackpotName} Jackpot Trigger activated`,
                            line_2: "",
                          },
                          style: {
                            icon: "tick circle success.png",
                            type: "Success",
                          },
                        };

                        translatedMsg.message.line_1 = `${host.jackpotName} ${translatedMsg?.message?.line_1}`;
                        host.authService.showToastMessage(translatedMsg, defaultMsg);
                      })
                      .catch((error) => {
                        return error;
                      });
                  } catch (e) {
                    return e;
                  }
                }
              },
              (err) => {
                return err;
              }
            );
        } else if (!userTokenExists) {
          const queryParams = host.actRoute.snapshot.queryParams || {};
          host.route.navigate(["/pre-auth"], { queryParams: { jackpotTrigger: "requested", ...queryParams } });
          host.paymentService.triggerActivatingData$.next(data);
          localStorage.setItem("JackpotTriggerData", JSON.stringify(data));
        } else {
          host.authService
            .getConstantsMsg("1074")
            .then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Activating Jackpot Trigger failed",
                  line_2: "",
                },
                style: {
                  icon: "tick circle error.svg",
                  type: "Error",
                },
              };
              host.authService.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch((error) => {
              return error;
            });
        }
      },
      (err) => {
        host.authService.getConstantsMsg("1013").then((translatedMsg) => {
          const defaultMsg = {
            message: {
              line_1: "Something went wrong (user)",
              line_2: "",
            },
            style: {
              icon: "tick circle error.svg",
              type: "Error",
            },
          };
          host.authService.showToastMessage(translatedMsg, defaultMsg);
        });

        return err;
      }
    );
  }
}
